import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { orchestratorApi, PipelineStats, CaseItem, DispatchItem, DashboardState, DASHBOARD_STATES } from '../services/orchestratorApi';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Play,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Inbox,
  Search,
  FileCheck,
  Loader2,
  Ban,
  ExternalLink,
  Zap,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

// State configuration for visual styling
const STATE_CONFIG: Record<DashboardState, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'INTAKE': { label: 'Intake', color: 'var(--color-text-secondary)', bgColor: 'var(--color-bg-elevated)', icon: Inbox },
  'GATHERING': { label: 'Gathering', color: 'var(--color-brand)', bgColor: 'var(--color-needs-human-tint)', icon: FileText },
  'READY_FOR_ANALYSIS': { label: 'Ready for Analysis', color: 'var(--color-brand)', bgColor: 'var(--color-needs-human-tint)', icon: Search },
  'ANALYZING': { label: 'Analyzing', color: 'var(--color-brand)', bgColor: 'var(--color-needs-human-tint)', icon: Loader2 },
  'ISSUES_PENDING': { label: 'Issues Pending', color: 'var(--color-brand)', bgColor: 'var(--color-needs-human-tint)', icon: AlertTriangle },
  'READY_FOR_CALCULATION': { label: 'Ready for Calculation', color: 'var(--color-verified)', bgColor: 'transparent', icon: FileCheck },
  'COMPLETE': { label: 'Complete', color: 'var(--color-verified)', bgColor: 'transparent', icon: CheckCircle }
};

// Action labels for dispatch items
const ACTION_LABELS: Record<string, string> = {
  'run_unified_analysis': 'Run Unified Analysis',
  'run_forensic': 'Run Forensic Analysis',
  'run_attorney': 'Run Attorney Analysis',
  'run_adjuster': 'Run Adjuster Analysis',
  'run_settlement': 'Run Settlement Calculation',
  'reprocess_affected': 'Reprocess Affected Agents'
};

export default function OrchestratorDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [pipelineStats, setPipelineStats] = useState<PipelineStats | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [totalCases, setTotalCases] = useState(0);
  const [dispatchQueue, setDispatchQueue] = useState<DispatchItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedState, setSelectedState] = useState<DashboardState | null>(
    (searchParams.get('state') as DashboardState) || null
  );
  const [showDispatchQueue, setShowDispatchQueue] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [pipeline, casesData, dispatch] = await Promise.all([
        orchestratorApi.getPipelineStats(),
        orchestratorApi.getCases({ 
          state: selectedState || undefined,
          limit: 50 
        }),
        orchestratorApi.getDispatchQueue({ limit: 20 })
      ]);
      
      setPipelineStats(pipeline);
      setCases(casesData.cases);
      setTotalCases(casesData.total);
      setDispatchQueue(dispatch.items);
      setPendingCount(dispatch.summary.pending_count);
    } catch (err: any) {
      console.error('Failed to fetch orchestrator data:', err);
      setError(err.message || 'Failed to load orchestrator data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle state filter change
  const handleStateFilter = (state: DashboardState | null) => {
    setSelectedState(state);
    if (state) {
      setSearchParams({ state });
    } else {
      setSearchParams({});
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Quick actions
  const handleRunAnalysis = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      await orchestratorApi.createDispatch(caseId, 'run_unified_analysis', 'HIGH');
      toast.success('Analysis job created');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create analysis job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveDispatch = async (dispatchId: string) => {
    // Approve triggers a real LLM analysis run that bills against API quotas.
    // Audit 2026-04-25 F2.64 — guard with confirm() until we have a proper
    // AlertDialog. Native confirm() is intentional minimum-viable safety:
    // any future Phase-2 work can swap it for a styled dialog.
    if (!window.confirm(
      'Approve this dispatch?\n\n' +
      'This will start a real LLM analysis run that bills against your API quota. ' +
      'Cannot be undone once it starts.'
    )) {
      return;
    }
    setActionLoading(dispatchId);
    try {
      await orchestratorApi.approveDispatch(dispatchId);
      toast.success('Dispatch approved and queued');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectDispatch = async (dispatchId: string) => {
    if (!window.confirm('Reject this dispatch? It will be permanently cancelled.')) {
      return;
    }
    setActionLoading(dispatchId);
    try {
      await orchestratorApi.cancelDispatch(dispatchId);
      toast.success('Dispatch cancelled');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          <span className="cs-body ml-2">Loading orchestrator...</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="orchestrator-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="cs-display">Case Orchestrator</h1>
            <p className="cs-meta mt-1">
              Manage case pipeline and automated workflows
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/issues')}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-secondary)', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
            >
              <AlertTriangle className="w-4 h-4" />
              Issue Tracker
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 rounded-lg flex items-center gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{error}</span>
            <button onClick={handleRefresh} className="cs-body ml-auto underline" style={{ color: 'var(--color-needs-human-text)' }}>
              Retry
            </button>
          </div>
        )}

        {/* Pipeline Overview */}
        {pipelineStats && (
          <div className="mb-6 rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }} data-testid="pipeline-overview">
            <div className="flex items-center justify-between mb-4">
              <h2 className="cs-h2">Pipeline Overview</h2>
              <div className="flex items-center gap-4">
                <span className="cs-body">
                  Total: <strong>{pipelineStats.total_cases}</strong> cases
                </span>
                {pipelineStats.total_stuck > 0 && (
                  <span className="cs-body flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    <AlertTriangle className="w-4 h-4" />
                    <strong>{pipelineStats.total_stuck}</strong> stuck
                  </span>
                )}
              </div>
            </div>
            
            {/* Pipeline Funnel */}
            <div className="flex items-stretch gap-1" data-testid="pipeline-funnel">
              {DASHBOARD_STATES.map((state, idx) => {
                const stateData = pipelineStats.states.find(s => s.state === state);
                const config = STATE_CONFIG[state];
                const Icon = config.icon;
                const isSelected = selectedState === state;
                const count = stateData?.count || 0;
                const stuckCount = stateData?.stuck_count || 0;
                
                return (
                  <button
                    key={state}
                    onClick={() => handleStateFilter(isSelected ? null : state)}
                    className={`
                      flex-1 p-3 rounded-lg transition-all relative group
                      ${isSelected
                        ? 'ring-2 ring-offset-2'
                        : ''
                      }
                    `}
                    style={
                      isSelected
                        ? { background: config.bgColor, ['--tw-ring-color' as any]: 'var(--color-border)' }
                        : { background: 'var(--color-bg-base)' }
                    }
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                    data-testid={`pipeline-state-${state.toLowerCase()}`}
                  >
                    <div className="flex flex-col items-center">
                      <span className="inline-flex mb-1" style={{ color: config.color }}>
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="cs-figure-lg">{count}</span>
                      <span className="cs-meta text-center">{config.label}</span>
                      {stuckCount > 0 && (
                        <span className="cs-chip absolute -top-1 -right-1 rounded-full w-5 h-5 flex items-center justify-center" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                          {stuckCount}
                        </span>
                      )}
                    </div>
                    {idx < DASHBOARD_STATES.length - 1 && (
                      <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 z-10" style={{ color: 'var(--color-text-dim)' }} />
                    )}
                  </button>
                );
              })}
            </div>
            
            {selectedState && (
              <div className="mt-3 flex items-center gap-2">
                <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                <span className="cs-body">
                  Filtered by: <strong>{STATE_CONFIG[selectedState].label}</strong>
                </span>
                <button
                  onClick={() => handleStateFilter(null)}
                  className="cs-body hover:underline ml-2"
                  style={{ color: 'var(--color-brand)' }}
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Case List */}
          <div className="lg:col-span-2 rounded-xl" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }} data-testid="case-list">
            <div className="p-4" style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
              <h2 className="cs-h2">
                Cases {selectedState ? `(${STATE_CONFIG[selectedState].label})` : '(Active)'}
              </h2>
              <p className="cs-meta">
                {totalCases} total • Click a case to view details
              </p>
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              {cases.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-dim)' }} />
                  <p className="cs-body">No cases found</p>
                </div>
              ) : (
                cases.map((caseItem) => {
                  const stateConfig = STATE_CONFIG[caseItem.state];
                  const StateIcon = stateConfig.icon;
                  
                  return (
                    <div
                      key={caseItem.id}
                      className="p-4 transition-colors"
                      style={{ borderBottom: '1px solid var(--color-border-faint)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      data-testid={`case-item-${caseItem.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/report/${caseItem.id}`)}
                              className="cs-body truncate"
                              style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-brand)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                            >
                              {caseItem.name}
                            </button>
                            {caseItem.is_stuck && (
                              <span className="cs-chip px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                                <Clock className="w-3 h-3" />
                                Stuck
                              </span>
                            )}
                          </div>
                          <div className="cs-meta flex items-center gap-3 mt-1">
                            <span className="flex items-center gap-1" style={{ color: stateConfig.color }}>
                              <StateIcon className="w-4 h-4" />
                              {stateConfig.label}
                            </span>
                            <span>•</span>
                            <span>{caseItem.days_in_state} days</span>
                            {caseItem.open_issues > 0 && (
                              <>
                                <span>•</span>
                                <span style={{ color: 'var(--color-needs-human-text)' }}>
                                  {caseItem.open_issues} issue{caseItem.open_issues > 1 ? 's' : ''}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Blocker/Next Action */}
                          <div className="cs-body mt-2">
                            {caseItem.blocker.type === 'blocker' && (
                              <span className="flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                                <Ban className="w-4 h-4" />
                                {caseItem.blocker.message}
                              </span>
                            )}
                            {caseItem.blocker.type === 'waiting' && (
                              <span className="flex items-center gap-1" style={{ color: 'var(--color-brand)' }}>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {caseItem.blocker.message}
                              </span>
                            )}
                            {caseItem.blocker.type === 'action' && (
                              <span style={{ color: 'var(--color-brand)' }}>
                                Next: {caseItem.blocker.message}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          {caseItem.state === 'READY_FOR_ANALYSIS' && (
                            <button
                              onClick={() => handleRunAnalysis(caseItem.id)}
                              disabled={actionLoading === caseItem.id}
                              className="cs-body flex items-center gap-1 px-3 py-1.5 rounded-lg disabled:opacity-50"
                              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                              data-testid={`run-analysis-${caseItem.id}`}
                            >
                              {actionLoading === caseItem.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                              Run Analysis
                            </button>
                          )}
                          {caseItem.state === 'ISSUES_PENDING' && (
                            <button
                              onClick={() => navigate(`/issues?report_id=${caseItem.id}`)}
                              className="cs-body flex items-center gap-1 px-3 py-1.5 rounded-lg"
                              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                              data-testid={`view-issues-${caseItem.id}`}
                            >
                              <AlertTriangle className="w-4 h-4" />
                              View Issues
                            </button>
                          )}
                          {caseItem.state === 'READY_FOR_CALCULATION' && (
                            <button
                              onClick={() => navigate(`/report/${caseItem.id}`)}
                              className="cs-body flex items-center gap-1 px-3 py-1.5 rounded-lg"
                              style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                            >
                              <FileCheck className="w-4 h-4" />
                              Generate Report
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/report/${caseItem.id}`)}
                            className="p-2 rounded-lg"
                            style={{ color: 'var(--color-text-dim)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Dispatch Queue Sidebar */}
          <div className="lg:col-span-1" data-testid="dispatch-queue">
            <div className="rounded-xl" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <button
                onClick={() => setShowDispatchQueue(!showDispatchQueue)}
                className="w-full p-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid var(--color-border-sub)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-base)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <h2 className="cs-h2">Pending Approvals</h2>
                  {pendingCount > 0 && (
                    <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      {pendingCount}
                    </span>
                  )}
                </div>
                {showDispatchQueue ? (
                  <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                ) : (
                  <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                )}
              </button>

              {showDispatchQueue && (
                <div className="max-h-[500px] overflow-y-auto">
                  {dispatchQueue.length === 0 ? (
                    <div className="p-6 text-center">
                      <CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-verified)' }} />
                      <p className="cs-body">No pending approvals</p>
                    </div>
                  ) : (
                    dispatchQueue.filter(d => d.status === 'PENDING_APPROVAL').map((dispatch) => (
                      <div key={dispatch.id} className="p-4" style={{ borderBottom: '1px solid var(--color-border-faint)' }} data-testid={`dispatch-item-${dispatch.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                              {ACTION_LABELS[dispatch.action] || dispatch.action}
                            </p>
                            <button
                              onClick={() => navigate(`/report/${dispatch.report_id}`)}
                              className="cs-meta hover:underline"
                              style={{ color: 'var(--color-brand)' }}
                            >
                              {dispatch.report_id}
                            </button>
                          </div>
                          <span
                            className="cs-chip px-2 py-0.5 rounded-full"
                            style={
                              dispatch.priority === 'CRITICAL' ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                              dispatch.priority === 'HIGH' ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                              { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                            }
                          >
                            {dispatch.priority}
                          </span>
                        </div>

                        <p className="cs-meta mb-3">
                          Triggered by: {dispatch.trigger}
                        </p>

                        {dispatch.affected_agents && dispatch.affected_agents.length > 0 && (
                          <div className="cs-meta mb-3">
                            Agents: {dispatch.affected_agents.join(', ')}
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveDispatch(dispatch.id)}
                            disabled={actionLoading === dispatch.id}
                            className="cs-body flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg disabled:opacity-50"
                            style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                            data-testid={`approve-dispatch-${dispatch.id}`}
                          >
                            {actionLoading === dispatch.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectDispatch(dispatch.id)}
                            disabled={actionLoading === dispatch.id}
                            className="cs-body flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg disabled:opacity-50"
                            style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-elevated)'; }}
                            data-testid={`reject-dispatch-${dispatch.id}`}
                          >
                            <Ban className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {dispatchQueue.filter(d => d.status !== 'PENDING_APPROVAL').length > 0 && (
                    <div className="p-3" style={{ background: 'var(--color-bg-base)' }}>
                      <p className="cs-meta text-center">
                        {dispatchQueue.filter(d => d.status !== 'PENDING_APPROVAL').length} other dispatches
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
